import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import type { AssistantToolInfo } from '@cumpli/shared';
import { MessagesSquare, PanelLeftClose, PanelLeftOpen, Plus, X } from 'lucide-react';
import { cn } from '../components/ui';
import { useAssistantTools, useMe } from '../lib/hooks';
import { useAssistantChat } from '../components/assistant';
import { Thread } from '../components/assistant/thread';
import { Composer } from '../components/assistant/composer';
import { ConversationList } from '../components/assistant/conversation-list';
import { ToolExplorer } from '../components/assistant/tool-explorer';
import { SlashMenu, filterTools } from '../components/assistant/slash-menu';
import { useStickToBottom } from '../lib/use-stick-to-bottom';

/**
 * /agente — the product's front door for the Cumplimiento world, now a threaded
 * workspace. On lg+ a conversations panel sits as a secondary rail between the
 * app nav sidebar and the thread; below lg it collapses to a "Conversaciones"
 * toggle that opens the list as a slide-over so small screens aren't crowded with
 * two rails. An empty active conversation shows the warm hero (greeting +
 * centered composer + chips); once it has messages, the hero collapses and the
 * composer docks to the bottom while the thread fills the column. The active
 * conversation is the app-wide one (shared with the ⌘K drawer via the provider),
 * so ⌘K anywhere shows the SAME thread.
 *
 * Two catalog-driven discovery surfaces sit on top of the composer, both fed by
 * the RBAC-filtered /assistant/tools endpoint (never re-filtered client-side):
 * - EMPTY STATE: the whole catalog as browsable capability cards (<ToolExplorer>),
 *   grouped Consultas / Acciones. Clicking one SEEDS the composer with its prompt.
 * - ANYTIME: typing "/" opens the <SlashMenu> above the composer — a filtered
 *   quick-invoke list that works whether or not a conversation exists. Both seed
 *   the composer (never auto-send); the agent then drives the normal flow.
 */

export default function Agente() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { data: me } = useMe();
  // RBAC-filtered capability catalog. On failure/empty the discovery surfaces
  // simply don't render — the composer alone still works, so the page never breaks.
  const { data: toolsData } = useAssistantTools();
  const tools = toolsData?.tools ?? [];
  const {
    msgs,
    thinking,
    retryCount,
    ask,
    stop,
    confirmAction,
    cancelAction,
    conversations,
    activeConversationId,
    newConversation,
    switchConversation,
    deleteConversation,
  } = useAssistantChat();

  const [input, setInput] = useState('');
  const [escDismissed, setEscDismissed] = useState(false);
  const [active, setActive] = useState(0);
  // Below lg, the conversations panel is a slide-over toggled from the agent area.
  const [convPanelOpen, setConvPanelOpen] = useState(false);
  // On lg+ the conversations rail is retractable — collapse it to a slim tab so the
  // thread gets full width. Persisted (like the world/switcher preference) so the
  // choice survives navigation and reloads.
  const [railOpen, setRailOpen] = useState<boolean>(
    () => localStorage.getItem('cumpli:agente-rail') !== 'closed'
  );
  const inputRef = useRef<HTMLInputElement>(null);
  const endRef = useRef<HTMLDivElement>(null);
  const threadRef = useRef<HTMLDivElement>(null);

  const firstName = me?.fullName.split(' ')[0];
  const hasConversation = msgs.length > 0;

  // "/" quick-invoke menu. Derived purely from the input + an Esc dismissal, so
  // there's no imperative open/close state to drift. It works ANYTIME (empty OR
  // active thread) — that's the whole point of "/". A bare "/" lists everything;
  // text after it filters the catalog by label/name.
  const slashQuery = input.startsWith('/') ? input.slice(1) : null;
  const filteredTools = slashQuery !== null ? filterTools(tools, slashQuery) : [];
  const slashOpen = slashQuery !== null && !escDismissed && filteredTools.length > 0;

  // The explore cards are the empty-state discovery surface (they REPLACE the old
  // hardcoded palette). They only show on a fresh, empty thread; the "/" menu is
  // the anytime counterpart, so the "suggestions hide after first message" gate holds.
  const showExplore = !hasConversation && tools.length > 0;

  // Reliably keep the thread pinned to the newest message/result. The page scrolls
  // at the WINDOW level (no scrollRef), so `block: 'end'` on endRef scrolls the
  // window; endRef carries a `scroll-mb-*` so the newest bubble lands ABOVE the
  // sticky docked composer, not under it. `stickHard` (last turn is the user's own
  // send) forces the scroll even if they'd scrolled up. See use-stick-to-bottom.
  useStickToBottom({
    contentRef: threadRef,
    endRef,
    active: hasConversation,
    watch: [msgs, thinking],
    stickHard: msgs[msgs.length - 1]?.role === 'user',
  });

  // Reset the highlighted "/" row as the query changes (the filtered list shifts).
  useEffect(() => {
    setActive(0);
  }, [slashQuery]);

  // Switching / starting a thread resets the composer (empty box for the new thread).
  useEffect(() => {
    setInput('');
  }, [activeConversationId]);

  // Seed the composer from a deep link (?q=…) — e.g. a matriz de riesgo scenario
  // chip. Seeds WITHOUT sending (the user reviews and presses enter), then strips
  // the param so a refresh/back doesn't re-seed. HashRouter keeps the query inside
  // the hash; useSearchParams reads it there. Defined after the thread-reset effect
  // so it wins on mount. re-running after setSearchParams({}) is a no-op (q gone).
  useEffect(() => {
    const q = searchParams.get('q');
    if (!q) return;
    fillComposer(q);
    setSearchParams({}, { replace: true });
  }, [searchParams, setSearchParams]);

  // Persist the rail's open/closed choice across navigation + reloads.
  useEffect(() => {
    localStorage.setItem('cumpli:agente-rail', railOpen ? 'open' : 'closed');
  }, [railOpen]);

  // ⌘⇧B / Ctrl⇧B toggles the conversations rail (matches ⌘⇧O for a new thread).
  useEffect(() => {
    function onKey(e: globalThis.KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.shiftKey && e.key.toLowerCase() === 'b') {
        e.preventDefault();
        setRailOpen((v) => !v);
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  function send(text: string) {
    if (!text.trim() || thinking) return;
    setInput('');
    void ask(text);
  }

  /** Seed the composer without sending, then keep focus so the user can edit/send. */
  function fillComposer(prompt: string) {
    setInput(prompt);
    setEscDismissed(false);
    inputRef.current?.focus();
  }

  /** Pick a "/" menu item: replace the "/…" text with the tool's prompt (no send). */
  function seedFromTool(tool: AssistantToolInfo) {
    fillComposer(tool.prompt);
  }

  function onComposerKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (slashOpen) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setActive((a) => (a + 1) % filteredTools.length);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActive((a) => (a - 1 + filteredTools.length) % filteredTools.length);
      } else if (e.key === 'Enter') {
        e.preventDefault();
        const tool = filteredTools[active];
        if (tool) seedFromTool(tool);
      } else if (e.key === 'Escape') {
        e.preventDefault();
        setEscDismissed(true);
      }
      return;
    }
    if (e.key === 'Enter') send(input);
  }

  const composer = (
    <Composer
      ref={inputRef}
      value={input}
      onChange={(v) => {
        setInput(v);
        setEscDismissed(false);
      }}
      onSubmit={() => send(input)}
      thinking={thinking}
      onStop={stop}
      onKeyDown={onComposerKeyDown}
    />
  );

  const slashMenu = slashOpen ? (
    <SlashMenu
      items={filteredTools}
      activeIndex={active}
      onSelect={seedFromTool}
      onActiveChange={setActive}
    />
  ) : null;

  const conversationList = (
    <ConversationList
      conversations={conversations}
      activeId={activeConversationId}
      onSelect={switchConversation}
      onNew={newConversation}
      onDelete={deleteConversation}
    />
  );

  return (
    <>
      {/* The rail is pulled flush to the app nav (cancels AppLayout's sm:px-6 on
          lg) and carries a right border so it reads as an attached secondary panel
          rather than floating in the content padding. */}
      <div className="flex gap-4 lg:gap-6">
        {/* CONVERSATIONS RAIL — retractable secondary column on lg+ */}
        {railOpen ? (
          <aside
            className="sticky top-[4.5rem] hidden h-[calc(100dvh-6rem)] w-[276px] shrink-0 flex-col border-r border-line pr-4 lg:flex lg:-ml-6 lg:pl-6"
            aria-label="Conversaciones"
          >
            <div className="mb-2 flex items-center justify-between pl-1">
              <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-ink-faint">
                Conversaciones
              </span>
              <button
                type="button"
                onClick={() => setRailOpen(false)}
                aria-label="Ocultar conversaciones"
                title="Ocultar conversaciones (⌘⇧B)"
                className="grid h-7 w-7 place-items-center rounded-lg text-ink-faint transition-colors hover:bg-panel hover:text-primary-dark focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
              >
                <PanelLeftClose className="h-4 w-4" strokeWidth={1.75} />
              </button>
            </div>
            <div className="min-h-0 flex-1">{conversationList}</div>
          </aside>
        ) : (
          /* Collapsed → a slim icon rail flush to the nav: expand + new thread. */
          <aside
            className="sticky top-[4.5rem] hidden h-[calc(100dvh-6rem)] w-[60px] shrink-0 flex-col items-center gap-1.5 border-r border-line pt-1 lg:flex lg:-ml-6 lg:pl-3"
            aria-label="Conversaciones (contraído)"
          >
            <button
              type="button"
              onClick={() => setRailOpen(true)}
              aria-label="Mostrar conversaciones"
              aria-expanded={false}
              title="Mostrar conversaciones (⌘⇧B)"
              className="grid h-9 w-9 place-items-center rounded-xl text-ink-soft transition-colors hover:bg-panel hover:text-primary-dark focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
            >
              <PanelLeftOpen className="h-[18px] w-[18px]" strokeWidth={1.75} />
            </button>
            <button
              type="button"
              onClick={() => newConversation()}
              aria-label="Nueva conversación"
              title="Nueva conversación (⌘⇧O)"
              className="relative grid h-9 w-9 place-items-center rounded-xl border border-primary/25 bg-primary/[0.06] text-primary-dark transition-colors hover:border-primary hover:bg-primary/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
            >
              <Plus className="h-[18px] w-[18px]" strokeWidth={2.25} />
            </button>
          </aside>
        )}

        {/* THREAD COLUMN */}
        <div className="flex min-h-[calc(100dvh-13rem)] min-w-0 flex-1 flex-col">
          {/* Mobile/tablet toggle — opens the conversations panel as a slide-over */}
          <div className="mb-4 lg:hidden">
            <button
              type="button"
              onClick={() => setConvPanelOpen(true)}
              className="inline-flex items-center gap-2 rounded-full border border-line-strong bg-panel px-3.5 py-2 text-sm font-semibold text-ink-soft transition-colors hover:border-primary hover:text-primary-dark focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
            >
              <MessagesSquare className="h-4 w-4" strokeWidth={1.75} />
              Conversaciones
              {conversations.length > 1 && (
                <span className="grid h-5 min-w-5 place-items-center rounded-full bg-primary/10 px-1.5 font-mono text-[10px] font-bold text-primary-dark">
                  {conversations.length}
                </span>
              )}
            </button>
          </div>

          {/* Center the hero+composer on an empty thread, but top-align once the
              explore cards are shown so the full catalog can flow/scroll. */}
          <div
            className={cn(
              'flex flex-1 flex-col',
              !hasConversation && (showExplore ? 'pt-2 sm:pt-6' : 'justify-center')
            )}
          >
            <div className="mx-auto w-full max-w-3xl">
              {/* HERO — collapses when the first message sends */}
              <div
                className={cn(
                  'flex flex-col items-center overflow-hidden text-center transition-all duration-700 ease-[cubic-bezier(.34,.72,.18,1)] motion-reduce:transition-none',
                  hasConversation ? 'max-h-0 opacity-0' : 'mb-8 max-h-[420px] opacity-100'
                )}
              >
                <span className="grid h-14 w-14 place-items-center rounded-2xl bg-gradient-to-br from-primary-mid to-primary-dark font-serif text-2xl font-semibold text-white shadow-[0_8px_20px_-8px_rgba(95,31,41,.6)]">
                  C
                </span>
                <h1 className="mt-4 font-serif text-3xl font-medium text-ink sm:text-4xl">
                  {firstName ? `Hola, ${firstName}` : 'Hola'}
                </h1>
                {/* Greeting prompt only on the first thread; once the user has chatted
                    (and started another thread) a new empty thread just shows the composer. */}
                {conversations.length <= 1 && (
                  <p className="mt-1.5 text-base text-ink-soft">¿En qué te ayudo hoy?</p>
                )}
              </div>

              {/* THREAD */}
              {hasConversation && (
                <div
                  ref={threadRef}
                  role="log"
                  aria-live="polite"
                  aria-relevant="additions"
                  aria-label="Conversación con Cumpli AI"
                  className="space-y-4 pb-32"
                >
                  <Thread
                    msgs={msgs}
                    thinking={thinking}
                    retryCount={retryCount}
                    variant="page"
                    senderName={firstName}
                    onConfirmAction={(i, j) => void confirmAction(i, j)}
                    onCancelAction={cancelAction}
                    onOpenEntity={(href) => navigate(href)}
                    onSeedComposer={fillComposer}
                  />
                  {/* scroll-mb clears the sticky docked composer so `block: 'end'`
                      lands the newest bubble above it, never behind it. */}
                  <div ref={endRef} className="scroll-mb-28" />
                </div>
              )}

              {/* CENTERED COMPOSER (empty state) */}
              {!hasConversation && (
                <>
                  <div className="mx-auto w-full max-w-2xl">
                    <div className="relative">
                      {slashMenu}
                      {composer}
                    </div>
                    <p className="mt-2.5 text-center text-xs text-ink-faint">
                      Escribe <span className="font-mono text-ink-soft">/</span> para invocar una
                      capacidad{showExplore ? ', o elige una de abajo.' : '.'}
                    </p>
                  </div>

                  {/* EXPLORE — the RBAC-filtered catalog as capability cards. Seeds
                      the composer (never auto-sends); write cards then go through
                      the propose → confirm flow. Hidden if the catalog is unavailable. */}
                  {showExplore && (
                    <ToolExplorer
                      tools={tools}
                      onPick={fillComposer}
                      className="mt-8"
                    />
                  )}
                </>
              )}
            </div>
          </div>

          {/* DOCKED COMPOSER (conversation) — column-scoped on the workspace, edge
              to edge on mobile where there is no rail. */}
          {hasConversation && (
            <div className="sticky bottom-0 z-10 -mx-4 border-t border-line/70 bg-canvas/85 px-4 pb-[env(safe-area-inset-bottom)] pt-3 backdrop-blur sm:-mx-6 sm:px-6 lg:mx-0">
              <div className="relative mx-auto w-full max-w-2xl pb-3">
                {slashMenu}
                {composer}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* CONVERSATIONS SLIDE-OVER (below lg) */}
      {convPanelOpen && (
        <div className="fixed inset-0 z-[60] lg:hidden" role="dialog" aria-modal="true" aria-label="Conversaciones">
          <div className="absolute inset-0 bg-ink/40 backdrop-blur-sm" onClick={() => setConvPanelOpen(false)} />
          <aside className="absolute inset-y-0 left-0 flex w-[85%] max-w-xs flex-col bg-canvas shadow-pop">
            <header className="flex items-center justify-between border-b border-line bg-panel px-4 py-3.5">
              <span className="font-serif text-base font-medium text-ink">Conversaciones</span>
              <button
                type="button"
                onClick={() => setConvPanelOpen(false)}
                aria-label="Cerrar"
                className="grid h-9 w-9 place-items-center rounded-full text-ink-soft transition-colors hover:bg-canvas"
              >
                <X className="h-5 w-5" strokeWidth={2} />
              </button>
            </header>
            <div className="min-h-0 flex-1 overflow-hidden p-3">
              <ConversationList
                conversations={conversations}
                activeId={activeConversationId}
                onSelect={(id) => {
                  switchConversation(id);
                  setConvPanelOpen(false);
                }}
                onNew={() => {
                  newConversation();
                  setConvPanelOpen(false);
                }}
                onDelete={deleteConversation}
              />
            </div>
          </aside>
        </div>
      )}
    </>
  );
}
